import type {
  IDateIntervalInput,
  IDeliveryHierarchyParentInput,
  ILocationBase,
  ILocationInput,
  ILocationWorkflowRestrictions,
} from '~/client/graph/types.generated'
import type { IResourceRecord } from '~/client/src/shared/resources/definitions/types'

import { getLocationKind } from './locationKind'
import type { ResourceResult } from './resourceResult'
import { resourceErr, resourceOk } from './resourceResult'

type LocationResourceDto = ILocationBase

/**
 * Per-kind fields no resource surface edits, carried through untouched so a save sends them
 * back. SaveLocations `$set`s every member of the entity, so an omitted one is written as
 * null: renaming a building would otherwise erase its level order.
 */
export type LocationKindFields = Pick<
  ILocationInput,
  | 'levelsOrder'
  | 'groundLevel'
  | 'isGroundIndexZero'
  | 'skipLevelIndexTemplate'
  | 'levelBaseName'
  | 'isNameImplicit'
  | 'accessibleLevels'
  | 'levelsClosures'
  | 'maxHeight'
  | 'maxWidth'
  | 'maxWeight'
  | 'isLinked'
>

const SCALAR_KIND_FIELDS = [
  'levelsOrder',
  'groundLevel',
  'isGroundIndexZero',
  'skipLevelIndexTemplate',
  'levelBaseName',
  'isNameImplicit',
  'accessibleLevels',
  'maxHeight',
  'maxWidth',
  'maxWeight',
  'isLinked',
] as const

export interface LocationResource {
  readonly id: string
  readonly name: string
  readonly isDeleted: boolean
  readonly code: string | null
  readonly stxResourceId: string | null
  readonly templateId: string | null
  readonly data: Readonly<Record<string, unknown>>
  readonly color: string
  readonly iconName: string
  readonly permittedCompanies: readonly string[]
  // The resource controls. isBookable is the WR toggle and defaults to true on the
  // entity - "bookable unless explicitly disabled" - so it has to be read from the
  // server rather than defaulted here, or every location reads as switched off.
  readonly isBookable: boolean
  readonly showInMap: boolean
  readonly workflowRestrictions: ILocationWorkflowRestrictions | null
  readonly maxOverlappingDeliveries: number | null
  readonly maxBookingDuration: number | null
  readonly assignedSitemaps: readonly (string | null)[]
  readonly assignedGlobes: readonly (string | null)[]
  readonly operatingIntervals: readonly IDateIntervalInput[]
  readonly parent: IDeliveryHierarchyParentInput | null
  /**
   * The kind slug, resolved once at parse time by {@link getLocationKind} — read from
   * `data.type` when the record has been migrated, derived from the legacy `type` enum
   * when it has not. Null for a location with neither, which the table renders blank
   * rather than guessing a kind.
   */
  readonly kind: string | null
  readonly kindFields: Readonly<LocationKindFields>
}

export interface MissingLocationResourceId {
  readonly _tag: 'MissingLocationResourceId'
}

const recordOf = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? { ...value }
    : {}

const idsOf = (values: readonly unknown[] | null | undefined): string[] =>
  (values ?? [])
    .filter(value => value !== null && value !== undefined)
    .map(String)

const nullableIdsOf = (
  values: readonly (string | null)[] | null | undefined,
): Array<string | null> => [...(values ?? [])]

// Only keys the query actually delivered are kept: an absent one stays absent rather than
// becoming an explicit null. Built fresh so no `__typename` reaches the input type.
function kindFieldsOf(location: LocationResourceDto): LocationKindFields {
  const source = location as unknown as Record<string, any>
  const fields: Record<string, unknown> = {}

  SCALAR_KIND_FIELDS.forEach(key => {
    const value = source[key]
    if (value === undefined) return
    fields[key] = Array.isArray(value) ? [...value] : value
  })

  if (Array.isArray(source.levelsClosures)) {
    fields.levelsClosures = source.levelsClosures.map(closure => ({
      levels: [...(closure?.levels ?? [])],
      interval: closure?.interval
        ? {
            startDate: closure.interval.startDate,
            endDate: closure.interval.endDate,
          }
        : null,
    }))
  }

  return fields as LocationKindFields
}

export function parseLocationResource(
  location: LocationResourceDto,
): ResourceResult<LocationResource, MissingLocationResourceId> {
  if (location.id === null || location.id === undefined) {
    return resourceErr({ _tag: 'MissingLocationResourceId' })
  }

  return resourceOk({
    id: String(location.id),
    name: location.name,
    isDeleted: !!location.isDeleted,
    code: location.code ?? null,
    stxResourceId: location.stxResourceId ?? null,
    templateId: location.templateId ?? null,
    data: recordOf(location.data),
    color: location.color,
    iconName: location.iconName ?? 'map-marker',
    permittedCompanies: idsOf(location.permittedCompanies),
    isBookable: location.isBookable,
    showInMap: location.showInMap,
    workflowRestrictions: location.workflowRestrictions ?? null,
    maxOverlappingDeliveries: location.maxOverlappingDeliveries ?? null,
    maxBookingDuration: location.maxBookingDuration ?? null,
    assignedSitemaps: nullableIdsOf(location.assignedSitemaps),
    assignedGlobes: nullableIdsOf(location.assignedGlobes),
    operatingIntervals: (location.operatingIntervals ?? []).flatMap(interval =>
      interval ? [{ ...interval }] : [],
    ),
    parent: location.parent
      ? {
          parentId:
            location.parent.parentId === null ||
            location.parent.parentId === undefined
              ? null
              : String(location.parent.parentId),
        }
      : null,
    kind: getLocationKind(location),
    kindFields: kindFieldsOf(location),
  })
}

export function toLocationResourceRecord(
  resource: LocationResource,
): IResourceRecord {
  const rootFields = {
    id: resource.id,
    name: resource.name,
    code: resource.code,
    stxResourceId: resource.stxResourceId,
    templateId: resource.templateId,
    color: resource.color,
    iconName: resource.iconName,
    parent: resource.parent,
  }

  return {
    ...rootFields,
    isDeleted: resource.isDeleted,
    // The kind is written into `data.type` so the Type column reads it the way every
    // other resource property is read. Pre-migration rows have nothing stored there, so
    // the value derived from the legacy enum stands in — same column, same shape, and it
    // keeps working unchanged once the migration fills the field in for real.
    data: {
      ...resource.data,
      ...(resource.kind ? { type: resource.kind } : {}),
    },
    rollups: {},
    root: {
      ...rootFields,
      kind: resource.kind,
      // The controls table and the card's Controls section read these off root.
      permittedCompanies: [...resource.permittedCompanies],
      isBookable: resource.isBookable,
      showInMap: resource.showInMap,
      workflowRestrictions: resource.workflowRestrictions,
      operatingIntervals: resource.operatingIntervals.map(i => ({ ...i })),
      maxOverlappingDeliveries: resource.maxOverlappingDeliveries,
      maxBookingDuration: resource.maxBookingDuration,
    },
  }
}
